/**
 * Subdomain → tenant slug detection.
 *
 * Production runs on TWO live base domains side by side:
 *   - `lovarthotels.ru` (new primary, Москва)
 *   - `lovarthotels.com` (legacy, под флагом Apple)
 * A slug is extracted if the host ends with ANY of them, so the move to
 * `.ru` is seamless and the frozen `.com` tests keep passing.
 *
 * Reserved subdomains `app`, `api`, `bot`, `static`, `www` are NOT tenant slugs.
 */

/** All base domains whose leading subdomain is a tenant slug. */
export const BASE_DOMAINS = ['lovarthotels.ru', 'lovarthotels.com'] as const;

/**
 * Primary base domain — kept for backwards compatibility with importers that
 * expect a single string. Equals the first entry of {@link BASE_DOMAINS}.
 */
export const BASE_DOMAIN = BASE_DOMAINS[0];

export const RESERVED_SUBDOMAINS = ['app', 'api', 'bot', 'static', 'www'] as const;

const SLUG_RE = /^([a-z0-9]([a-z0-9-]*[a-z0-9])?)$/;

/**
 * Extracts the tenant slug from a Host header / hostname.
 *
 * Returns the leading subdomain when the host ends with one of
 * {@link BASE_DOMAINS} and the subdomain is a single, valid, non-reserved
 * label. Otherwise `null` (apex, www / reserved, nested subdomain, localhost,
 * unknown domain).
 */
export function parseTenantSlugFromHost(host: string): string | null {
  const hostname = host.split(':')[0]?.toLowerCase() ?? '';
  if (!hostname) return null;

  for (const base of BASE_DOMAINS) {
    const suffix = `.${base}`;
    if (!hostname.endsWith(suffix)) continue;
    const sub = hostname.slice(0, -suffix.length);
    if (!sub || sub.includes('.')) return null;
    if ((RESERVED_SUBDOMAINS as readonly string[]).includes(sub)) return null;
    if (!SLUG_RE.test(sub)) return null;
    return sub;
  }
  return null;
}
